import DataQuery from 'data/DataQuery';
import { Customer_ContractDropdown, Enums_CapacityGasType } from 'data/endpoints';
import { useQuery } from 'data/hooks/useQuery';
import PostResponse from 'data/PostResponse';
import { formatISO } from 'date-fns';
import ContractSelect from 'features/contracts/components/ContractSelect';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import { CapacityGasType } from 'features/contracts/enums/capacityGasType';
import { ContractStatus } from 'features/contracts/enums/contractStatus';
import { ContractType } from 'features/contracts/enums/contractType';
import InsertRequirementResultDTO from 'features/requirements/dtos/InsertRequirementResultDTO';
import ReservedCapacityGasPeriodFromDTO from 'features/requirements/dtos/ReservedCapacityGasPeriodFromDTO';
import { openRequirementResultDialog } from 'features/requirements/helpers/requirementResultDialogHelper';
import BaseRequirementFormProps from 'features/requirements/models/BaseRequirementFormProps';
import { RequirementResultDialogParams } from 'features/requirements/models/RequirementResultDialogParams';
import IRequirementCreateService from 'features/requirements/services/IRequirementCreateService';
import IRequirementFormService from 'features/requirements/services/IRequirementFormService';
import IReservedCapacityGasFormService from 'features/requirements/services/IReservedCapacityGasFormService';
import PageCard from 'features/shared/components/card/PageCard';
import { ReservedCapacityGasFormSection_Capacity } from 'features/shared/components/loaders/promiseLoadingAreas';
import PromiseLoadingSpinner from 'features/shared/components/loaders/PromiseLoadingSpinner';
import DatePicker from 'features/shared/components/reactDates/DatePicker';
import RenderWrapper from 'features/shared/components/renderWrapper/RenderWrapper';
import SelectInput from 'features/shared/components/selectInput/SelectInput';
import { UserEnumDTO } from 'features/shared/enums/dynamic/UserEnumDTO';
import FormGroup from 'features/shared/forms/components/FormGroup';
import FormGroupTooltip from 'features/shared/forms/components/formGroup/FormGroupTooltip';
import { useForm } from 'features/shared/forms/hooks/useForm';
import { getQueryParams } from 'features/shared/helpers/queryParams';
import { getContractDetailRootRoute } from 'features/shared/helpers/routeHelper';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import { Nullable } from 'features/shared/types/nullable';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import IAppState from 'infrastructure/store/IAppState';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import BasicInfoSubFormSection from '../basicInfoSubForm/BasicInfoSubFormSection';
import { reservedCapacityGasFormValidations } from './reservedCapacityGasFormValidation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReservedCapacityInfoTooltip from 'features/contracts/components/reservedCapacity/ReservedCapacityInfoTooltip';

interface Props extends BaseRequirementFormProps {
  selectedContractId?: string;
}

const ReservedCapacityGasFormSection = (props?: Props) => {
  //#region translations
  const tLabels = useTranslation("requirements\\requirements").t;
  const tTerms = useTranslation("shared\\terms").t;
  //#endregion translations

  //#region DI
  const createService = useInjection<IRequirementCreateService>(IocTypes.RequirementCreateService);
  const formService = useInjection<IReservedCapacityGasFormService>(IocTypes.ReservedCapacityGasFormService);
  const baseFormService = useInjection<IRequirementFormService>(IocTypes.RequirementFormService);
  //#endregion DI

  // #region router location
  const { state: selectedContractIdLocation  } = useLocation<string>();
  // //#endregion router location

  //#region Props
  const useOutsideOfRequirements = props?.useOutsideOfRequirements ? true : false;
  const sendButtonTitle = props?.sendButtonTitle ? props.sendButtonTitle : tLabels("reservedCapacityGasFormSection.sendRequirement");
  const selectedContractId = props?.selectedContractId ? props.selectedContractId : selectedContractIdLocation;
  //#endregion Props

  //#region store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;
  //#endregion store

  //#region state
  const [selectedContract, setSelectedContract] = useState<ContractDropdownDTO>({});
  const [selectedDateFrom, setSelectedDateFrom] = useState<Nullable<Date>>(null);
  const [selectedDateTo, setSelectedDateTo] = useState<Nullable<Date>>(null);
  const [gasPeriodFrom, setGasPeriodFrom] = useState<ReservedCapacityGasPeriodFromDTO>({});
  const [minFromDate, setMinFromDate] = useState<Nullable<Date>>(null);
  //#endregion state

  //#region fetch data
  let contractsQueryParams = getQueryParams("ContractStatus", [ContractStatus.Effective, ContractStatus.Valid]);
  contractsQueryParams.append("ContractType", ContractType.GasLargeConsumption);
  contractsQueryParams.append("MeterType", "A");
  contractsQueryParams.append("MeterType", "B");

  let contractsQuery: DataQuery = {
    url: Customer_ContractDropdown,
    routeParams: getRouteParams("customerId", customer.customerId!),
    params: contractsQueryParams
  };
  const { data: contracts } = useQuery<ContractDropdownDTO[]>(contractsQuery, [], [customer]);

  const { data: capacityTypes } = useQuery<UserEnumDTO[]>({ url: Enums_CapacityGasType }, []);
  //#endregion fetch data

  //#region form hook
  const {
    values,
    setFormValue,
    errors,
    bindField,
    bindUncontrolledField,
    valideteForm,
    validateFormField,
    setValidationRule,
    clearValidationErrors
  } = useForm({
    initialValidations: reservedCapacityGasFormValidations(),
  });
  //#endregion

  //#region side-effects
  useEffect(() => {
    baseFormService.setDefaultContract(contracts, contractChangeHandler, selectedContractId);
  }, [selectedContractId, contracts])
  //#endregion

  // #region handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (valideteForm()) {
      let valuesToSave = formService.prepareValuesToSave(values);
      let result = await createService.createRequirement("ZMENAKAPACITYPLYN", valuesToSave);
      openResultDialogHandler(result);
    }
  };

  const openResultDialogHandler = (result: PostResponse<InsertRequirementResultDTO>) => {
    const params: RequirementResultDialogParams = {
      response: result,
      successMessage: tLabels("reservedCapacityGasFormSection.reservedCapacitySuccessMessage"),
      errorMessage: tLabels("reservedCapacityGasFormSection.reservedCapacityErrorMessage"),
      actionBtn: { title: tLabels("reservedCapacityGasFormSection.reservedCapacity"), route: getContractDetailRootRoute(values["DESC_TMSMLOUVAID"], "reserved-capacity") },
      transitonBtn: null
    };

    openRequirementResultDialog(useOutsideOfRequirements, params, tLabels("reservedCapacityGasFormSection.changeReservedCapacity"));
  }

  const contractChangeHandler = async (contract: ContractDropdownDTO | null) => {
    if (contract) {
      // set state
      setSelectedContract(contract);
      // set select data to form values
      setFormValue("DESC_TMSMLOUVAID", contract.contractId);
      // clear CapacityType
      baseFormService.clearFormValues(["DESC_TMKAPACITAPLYNTYP"], setFormValue);
      // clear validaion errors
      clearValidationErrors(["DESC_TMSMLOUVAID", "DESC_TMKAPACITAPLYNTYP"]);
    }
  }

  const capacityTypeChangeHandler = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    let typeCode = e.target.value;

    if (validateFormField("DESC_TMKAPACITAPLYNTYP", typeCode)) {
      // clear form values
      baseFormService.clearFormValues(["DESC_TMKAPACITAPLYNPOZADOVANAKAPACITA", "DESC_TMKAPACITAPLYNOD", "DESC_TMKAPACITAPLYNDO"], setFormValue);
      // get dates by type
      let periodFrom = await trackPromise(formService.fetchGasPeriodFrom(typeCode), ReservedCapacityGasFormSection_Capacity)
      let dates = formService.getDatesByCapacityType(typeCode, periodFrom);
      // set default dates by type
      setSelectedDateFrom(dates[0]);
      setSelectedDateTo(dates[1]);
      setMinFromDate(dates[0]);
      setGasPeriodFrom(periodFrom);
      // set form values
      setFormValue("DESC_TMKAPACITAPLYNTYP", typeCode);
      // dates
      if (dates[0] !== null) {
        setFormValue("DESC_TMKAPACITAPLYNOD", formatISO(dates[0], { representation: "date"} ));
      }  
      if (dates[1] !== null) {
        setFormValue("DESC_TMKAPACITAPLYNDO", formatISO(dates[1], { representation: "date"} ));
      }
      // clear validation errors
      clearValidationErrors([
        "DESC_TMKAPACITAPLYNTYP",
        "DESC_TMKAPACITAPLYNOD",
        "DESC_TMKAPACITAPLYNDO",
        "DESC_TMKAPACITAPLYNPOZADOVANAKAPACITA",
      ]);
    }
  }

  const dateFromChangeHandler = (momentDate: Nullable<moment.Moment>) => {
    if (momentDate) {
      const date = momentDate.toDate();
      // set From date
      setSelectedDateFrom(date);
      setFormValue("DESC_TMKAPACITAPLYNOD", formatISO(date, { representation: "date"} ));
      // set To date
      const toDate = formService.getToDateByFromDate(date, values["DESC_TMKAPACITAPLYNTYP"]);
      setSelectedDateTo(toDate);
      setFormValue("DESC_TMKAPACITAPLYNDO", formatISO(toDate, { representation: "date"} ));    
    }
  }

  const dateToChangeHandler = (date: Nullable<moment.Moment>) => {
    if (date) {
      setSelectedDateTo(date.toDate());
      setFormValue("DESC_TMKAPACITAPLYNDO", date.format(moment.HTML5_FMT.DATE));   
    }
  }
  // #endregion handlers

  //#region helpers
  const getFromTooltip = () => { 
    let text = tLabels("reservedCapacityGasFormSection.slidingCapacityTooltip");

    if (values["DESC_TMKAPACITAPLYNTYP"] == CapacityGasType.Sliding && text !== "") {
      let text = tLabels("reservedCapacityGasFormSection.slidingCapacityTooltip");
      let tooltip: FormGroupTooltip = {
        id: "SlidingCapacityTooltip",
        content: text
      }

      return tooltip;
    }
  }
  // #endregion helpers

  //#region render
  return (
    <form onSubmit={submitFormHandler}>
      <PageCard 
        title={tLabels("reservedCapacityGasFormSection.cardTitle")} 
        bodyClassName="pt-4 px-4"
      >
        <div className="row no-gutters mx-n2 position-relative">
          <div className="col-lg-6 px-2">
            <ContractSelect 
              name="DESC_TMSMLOUVAID"
              contracts={contracts}
              onChange={contractChangeHandler}
              errors={errors}
              defaultContract={selectedContract}
            />
          </div>
          <div className="col-sm-6 col-lg-6 px-2">
            <SelectInput 
              name="DESC_TMKAPACITAPLYNTYP"
              options={capacityTypes} 
              optionName="name"
              optionValue="code"
              label={tLabels("reservedCapacityGasFormSection.reservedCapacityType")}
              defaultValueLabel={tLabels("reservedCapacityGasFormSection.setReservedCapacityType")}
              defaultValueLabelDisabled
              onChange={capacityTypeChangeHandler}
              value={values["DESC_TMKAPACITAPLYNTYP"] ?? ""}
              visible={baseFormService.isFilledValues(values, ["DESC_TMSMLOUVAID"])}
              validationError={errors ? errors["DESC_TMKAPACITAPLYNTYP"] : undefined}
            />
          </div>
          <div className="col-lg-6 px-2">
              <FormGroup
                labelText={values["DESC_TMKAPACITAPLYNTYP"] === "Daily" ? tLabels("reservedCapacityGasFormSection.reservedCapacityDailyFrom") : tTerms("From")}
                visible={baseFormService.isFilledValues(values, ["DESC_TMKAPACITAPLYNTYP"])}
                validationError={errors ? errors["DESC_TMKAPACITAPLYNOD"] : undefined}
                tooltip={getFromTooltip()}
              >
                <DatePicker 
                  name="DESC_TMKAPACITAPLYNOD"
                  onChange={dateFromChangeHandler}
                  selectedDate={selectedDateFrom}
                  onlySelectable
                  isDayOutsideOfRange={formService.getDateFromIsOutsideOfRangeFunction(values["DESC_TMKAPACITAPLYNTYP"], minFromDate, gasPeriodFrom.to!)}
                />
              </FormGroup>
            </div>
            <RenderWrapper
              isVisible={() =>
                baseFormService.isFilledValues(values, [
                  "DESC_TMKAPACITAPLYNTYP",
                ]) && values["DESC_TMKAPACITAPLYNTYP"] !== "Daily"
              }
            >
              <div className="col-lg-6 px-2">
                <FormGroup
                  labelText={tTerms("To")}
                  validationError={errors ? errors["DESC_TMKAPACITAPLYNDO"] : undefined}
                >
                  <DatePicker 
                    name="DESC_TMKAPACITAPLYNDO"
                    onChange={dateToChangeHandler}
                    selectedDate={selectedDateTo}
                    onlySelectable
                    disabled={values["DESC_TMKAPACITAPLYNTYP"] !== "Sliding"}
                    isDayOutsideOfRange={formService.getDateToIsOutsideOfRangeFunction(values["DESC_TMKAPACITAPLYNTYP"], selectedDateFrom)}
                  />
                </FormGroup>
              </div>
            </RenderWrapper>
            <div className="col-sm-6 px-2">
              <FormGroup 
                labelText={tLabels("reservedCapacityGasFormSection.requiredCapacity.label")} 
                visible={baseFormService.isFilledValues(values, ["DESC_TMKAPACITAPLYNTYP"])}
                validationError={errors["DESC_TMKAPACITAPLYNPOZADOVANAKAPACITA"]}
              >
                <div className="input-group">
                  <input 
                    className="form-control" 
                    placeholder={tLabels("reservedCapacityGasFormSection.requiredCapacity.placeholder")}
                    {...bindField("DESC_TMKAPACITAPLYNPOZADOVANAKAPACITA")} 
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">m<sup>3</sup></span>
                  </div>
                </div>
              </FormGroup>
          </div>
          <PromiseLoadingSpinner area={ReservedCapacityGasFormSection_Capacity} delay={100} />
        </div>
      </PageCard>

      <RenderWrapper isVisible={() => !useOutsideOfRequirements}>
        <BasicInfoSubFormSection
          bindField={bindField}
          bindUncontrolledField={bindUncontrolledField}
          errors={errors}
          setValidationRule={setValidationRule}
        />
      </RenderWrapper>

      <div className="d-inline-flex align-items-center">
        <button type="submit" className="btn btn-primary px-3 py-2">
          {sendButtonTitle}
        </button>
        <ReservedCapacityInfoTooltip type="gas" />
      </div>
    </form>
  )
  //#endregion render
}

export default ReservedCapacityGasFormSection;